import { Box, Button, Card, CardContent, Chip, Divider, Stack, Typography, alpha } from "@mui/material";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import ExploreOutlined from "@mui/icons-material/ExploreOutlined";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import StarRounded from "@mui/icons-material/StarRounded";
import BusinessCenterRounded from "@mui/icons-material/BusinessCenterRounded";
import WorkspacePremiumRounded from "@mui/icons-material/WorkspacePremiumRounded";
import type { PlanId } from "@ebook/contracts";
import { plans } from "../lib/plans";
import { useTranslate } from "../i18n";

const PLAN_ICONS: Record<PlanId, typeof ExploreOutlined> = {
  free: ExploreOutlined,
  starter: TrendingUpRounded,
  pro: StarRounded,
  business: BusinessCenterRounded,
  enterprise: WorkspacePremiumRounded,
};

const GOLD = "#e9c46a";

type PlanGridProps = {
  mode: "marketing" | "picker";
  currentPlanId?: PlanId | null;
  onSelect?: (id: PlanId) => void;
  selecting?: PlanId | null;
  ctaHref?: string;
};

function FeatureRow({ label, value, tone }: { label: string; value: string; tone: "light" | "premium" }) {
  const iconColor = tone === "premium" ? GOLD : "success.main";
  const textColor = tone === "premium" ? "rgba(255,255,255,.92)" : "text.primary";
  const labelColor = tone === "premium" ? "rgba(255,255,255,.6)" : "text.secondary";
  return <Box display="flex" alignItems="center" gap={1}>
    <CheckRounded sx={{ fontSize: 18, color: iconColor, flexShrink: 0 }} />
    <Typography variant="body2" color={labelColor} sx={{ flex: 1 }}>{label}</Typography>
    <Typography variant="body2" fontWeight={700} color={textColor}>{value}</Typography>
  </Box>;
}

export function PlanGrid({ mode, currentPlanId, onSelect, selecting, ctaHref = "/login" }: PlanGridProps) {
  const __ = useTranslate();
  return <Box
    sx={{
      display: "grid",
      gap: 2.5,
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        md: "repeat(3, 1fr)",
        lg: "repeat(5, 1fr)",
      },
      alignItems: "stretch",
      pt: 2,
    }}
  >
    {plans.map((plan) => {
      const isCurrent = currentPlanId === plan.id;
      const Icon = PLAN_ICONS[plan.id];
      const tone = plan.premium ? "premium" : "light";
      return <Card
        key={plan.id}
        variant="outlined"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "visible",
          transition: "transform .2s ease, box-shadow .2s ease",
          "&:hover": { transform: "translateY(-4px)", boxShadow: (theme) => theme.palette.mode === "dark" ? "0 20px 40px rgba(0,0,0,.5)" : "0 20px 40px rgba(15,23,42,.12)" },
          ...(plan.highlight && {
            borderColor: "primary.main",
            borderWidth: 2,
            boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
          }),
          ...(plan.premium && {
            color: "#fff",
            borderColor: alpha(GOLD, 0.45),
            backgroundImage: "linear-gradient(160deg, #1b1c26 0%, #0c0d13 65%, #05060a 100%)",
            boxShadow: "0 24px 60px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.05)",
          }),
        }}
      >
        {plan.highlight && (
          <Chip
            label={__("แนะนำ")}
            color="primary"
            size="small"
            sx={{ position: "absolute", top: -14, left: 20, fontWeight: 800, letterSpacing: ".02em", whiteSpace: "nowrap", maxWidth: "calc(100% - 32px)" }}
          />
        )}
        {plan.premium && (
          <Chip
            label={__("พรีเมียม")}
            size="small"
            sx={{
              position: "absolute",
              top: -14,
              left: 20,
              fontWeight: 800,
              letterSpacing: ".02em",
              whiteSpace: "nowrap",
              maxWidth: "calc(100% - 32px)",
              bgcolor: GOLD,
              color: "#241c04",
            }}
          />
        )}
        <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", p: 3 }}>
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              width: 44,
              height: 44,
              borderRadius: 2.5,
              mb: 2,
              color: plan.premium ? GOLD : "primary.main",
              bgcolor: (theme) => plan.premium
                ? alpha(GOLD, 0.14)
                : alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.1),
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Typography variant="h6" fontWeight={800}>{plan.name}</Typography>
          <Typography
            variant="body2"
            sx={{ minHeight: 40, mt: 0.5, color: plan.premium ? "rgba(255,255,255,.62)" : "text.secondary" }}
          >
            {plan.tagline}
          </Typography>
          <Typography variant="h5" fontWeight={800} mt={2} sx={{ color: plan.premium ? "#fff" : "text.primary" }}>
            {plan.priceLabel}
          </Typography>
          <Divider sx={{ my: 2.5, borderColor: plan.premium ? "rgba(255,255,255,.12)" : "divider" }} />
          <Stack spacing={1.5} flex={1}>
            <FeatureRow
              label={__("จำนวนเล่ม")}
              value={plan.books === null ? __("ไม่จำกัด") : __("{{count}} เล่ม", { count: plan.books })}
              tone={tone}
            />
            <FeatureRow
              label={__("หน้า/เล่ม")}
              value={plan.pagesPerBook === null ? __("ไม่จำกัด") : __("{{count}} หน้า", { count: plan.pagesPerBook })}
              tone={tone}
            />
          </Stack>
          {mode === "marketing" ? (
            <Button
              component="a"
              href={ctaHref}
              variant={plan.highlight || plan.premium ? "contained" : "outlined"}
              fullWidth
              sx={{
                mt: 3,
                ...(plan.premium && {
                  bgcolor: GOLD,
                  color: "#241c04",
                  "&:hover": { bgcolor: "#dab04e" },
                }),
              }}
            >
              {__("เริ่มต้นใช้งาน")}
            </Button>
          ) : (
            <Button
              variant={isCurrent ? "outlined" : plan.highlight || plan.premium ? "contained" : "outlined"}
              fullWidth
              disabled={isCurrent || selecting === plan.id}
              onClick={() => onSelect?.(plan.id)}
              startIcon={isCurrent ? <CheckCircleRounded /> : undefined}
              sx={{
                mt: 3,
                ...(plan.premium && !isCurrent && {
                  bgcolor: GOLD,
                  color: "#241c04",
                  "&:hover": { bgcolor: "#dab04e" },
                }),
                ...(plan.premium && isCurrent && {
                  borderColor: "rgba(255,255,255,.4)",
                  color: "#fff",
                }),
              }}
            >
              {isCurrent ? __("แผนปัจจุบัน") : selecting === plan.id ? __("กำลังเปลี่ยน…") : __("เลือกแผนนี้")}
            </Button>
          )}
        </CardContent>
      </Card>;
    })}
  </Box>;
}
